<template>
  <CommentHistory
    hide-header
    class="comment-history-container"
    :comments="comments"
    :current-user="currentUser"
    :recipients="recipients"
    :selected-id="selectedCommentId"
    :hovered-id="hoveredCommentId"
    :sender="sender"
    :read-only="preventCommentsModification"
    :comments-hidden="commentsHidden"
    @create-reply="handleCreateReply($event)"
    @select="select"
    @update="updateComment($event)"
    @go-to-comment="goToComment($event)"
    @click-outside="handleClickOutside"
  />
</template>

<script lang="ts">
import CommentHistory from '@getaccept/lib-shared/src/contextual-commenting/components/CommentHistory.vue';
import { useContextualCommentingStore } from '@getaccept/lib-shared/src/contextual-commenting/store/contextual-commenting.store';
import type { CreateReplyInput } from '@getaccept/lib-shared/src/contextual-commenting/types/create-reply-input';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import { ButtonType, Placement } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useScrollToComment } from '@getaccept/lib-shared/src/contextual-commenting/composables/scroll-to-comment.composable';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { useContextualCommentingData } from '../composables/contextual-commenting-data.composable';

export default defineComponent({
  components: {
    CommentHistory,
  },
  props: {
    recipients: {
      type: Array as PropType<SigningPageRecipient[]>,
      required: true,
    },
    sender: {
      type: Object as PropType<User>,
      required: true,
    },
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
    disabled: Boolean,
    preventCommentsModification: Boolean,
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { isSmallScreenAndUp } = useBreakpoints();

    const currentUser = computed(() => ({
      recipientId: props.recipient.id,
      name: props.recipient.fullName,
      thumbUrl: props.recipient.thumbUrl,
    }));

    const { updateComment, createReply, selectComment } = useContextualCommentingData();

    const handleCreateReply = (payload: CreateReplyInput) => {
      createReply(payload, { userId: null, recipientId: currentUser.value.recipientId });
    };

    const {
      commentHistoryComments: comments,
      selectedCommentId,
      hoveredCommentId,
      isCommentHistoryOpen,
      commentsHidden,
    } = storeToRefs(useContextualCommentingStore());

    const isSelected = (commentId: string) => commentId === selectedCommentId.value;

    const select = (commentId: string) => {
      if (isSelected(commentId)) {
        selectComment(commentId, props.recipient.id);
      }
      goToComment(commentId);
      emit('close');
    };

    const handleClickOutside = (commentId: string) => {
      const ignoreClickOutside = !isSelected(commentId) || !isCommentHistoryOpen.value;
      if (ignoreClickOutside) return;

      select('');
    };

    const { scrollToComment } = useScrollToComment();

    const goToComment = (commentId: string) => {
      scrollToComment(commentId, isSmallScreenAndUp.value);
      emit('close');
    };

    return {
      select,
      updateComment,
      handleCreateReply,
      goToComment,
      t,
      handleClickOutside,
      ButtonType,
      comments,
      currentUser,
      selectedCommentId,
      hoveredCommentId,
      isCommentHistoryOpen,
      commentsHidden,
      Placement,
    };
  },
});
</script>
<style lang="scss" scoped>
.comment-history-container {
  max-height: 100%;
}
</style>
